/* ==========================================================================
   CRTZ - MOBILE STICKY HEADER
   Loaded from layout/theme.liquid via a single <script> tag.
   Delete that tag (and this file) to revert completely.

   Pins the mobile header (hamburger, logo, cart) to the top of the viewport
   and holds it at ONE fixed size. There is no scroll-driven resizing: the
   header looks identical at every scroll position.

   Tuning knobs are LOGO_SCALE / HEADER_BG / CART_NUDGE immediately below.

   Notes on this theme's quirks, so future edits don't re-learn them the hard way:
     - The mobile header is TWO sections. The hamburger + cart live in
       #shopify-section-header; the logo lives in #shopify-section-sidebar and is
       dragged up into the header row by a negative top margin.
     - #HeaderLogoWrapper is a DUPLICATED id. The copy inside the header section
       is display:none on mobile; the rendered one is in the sidebar.
     - That logo gets its width from a max-width plus auto side margins inside a
       fixed-width parent. Moved into an absolutely positioned box it has no
       intrinsic width (the <a> is an aspect-ratio padding box, the <img> inside
       is absolutely positioned) and collapses to 0. Hence the explicit width.
     - The icons overflow the header section's box. That is invisible while the
       header is transparent, but an opaque header needs its background extended
       past the box, which is what the ::after extension does.
     - The theme's mobile menu scroll-locks the page in a way that breaks
       position:sticky. See the html.menu-open rules below.
   ========================================================================== */
(function () {
  'use strict';

  /* Logo size relative to the theme's own. 0.65 == 35% smaller. */
  var LOGO_SCALE = 0.65;

  /* Hamburger and cart, same idea. 0.94 == 6% smaller - the theme's own size
     reads slightly heavy next to the reduced logo. Anchored to the outer edge
     and the vertical centre, so shrinking these does not move them away from
     the screen edge or off the shared centre line. */
  var ICON_SCALE = 0.94;

  /* Header background:
       'always' - solid black. Content scrolls underneath and is hidden by it,
                  so the logo and icons always read against a clean bar rather
                  than against whatever product happens to be passing behind.
       'never'  - transparent; the icons float over the content.
     The menu-open state is always solid regardless, since the header has to
     read against the open menu.

     When solid, the black extends past the section's own box to cover the icons
     that overflow it, and .crtz-header-space reserves that FULL height - so
     nothing is clipped at rest, and content only disappears once it scrolls up
     under the bar. */
  var HEADER_BG = 'never';
  var BG_SOLID  = (HEADER_BG === 'always');

  /* The cart artwork is 25px wide against the hamburger's 30px, so it reads as
     the smaller of the two. 25 x 1.2 = 30 matches them. Set to 1 to leave the
     icons exactly as the theme draws them. */
  var CART_NUDGE = 1.2;

  /* Tap targets. The theme's own hit boxes are 30x20 (hamburger) and 25x25
     (cart), both under the 44x44 guideline. An inset pseudo-element rebuilds
     them without moving anything visually. */
  var TAP_INSET = 16;

  /* Black above and below the icon/logo cluster. The theme drops its icons well
     down inside the header box, which on an opaque bar reads as a big gap on top
     and none underneath - so the cluster is lifted and the bar sized around it,
     giving PAD_Y on both sides. Measured from UNDER the red rule: that rule is
     its own design element, not padding. */
  var PAD_Y = 14;

  var MQ  = '(max-width: 749px)';
  var PAD = 4;    /* px of extra background below the lowest element */

  /* The Venn Apps native wrapper hides the web header entirely - stay out of it. */
  try {
    if (new URLSearchParams(location.search).get('sales_channel') === 'venn_apps') return;
  } catch (e) {}

  /* ---- styles ------------------------------------------------------------ */

  /* The theme fades its menu (#SiteNav) with `opacity .3s`. The header's
     background is the only thing that ever animates now - on menu open/close -
     so matching that timing exactly keeps the two backgrounds in step. */
  var MENU_FADE = '.3s';
  var MENU_EASE = 'ease';

  var CSS = [
    '@media screen and (max-width:749px){',

      /* Fixed rather than sticky, ALWAYS - with .crtz-header-space holding its
         place in the flow. Sticky would be the obvious choice, but the theme's
         mobile menu pins the page with body{position:fixed}, which stops sticky
         working and forces a switch to fixed exactly when the menu opens. That
         switch pulls the header out of the flow mid-interaction and everything
         below jumps up by its height. Staying fixed the whole time means opening
         the menu changes no layout at all.

         top is offset past any sticky banner above us - see stickyBarsAbove(). */
      '#shopify-section-header{',
        'position:fixed;top:var(--crtz-top,0px);left:0;right:0;z-index:10000;',
        'background-color:' + (BG_SOLID ? '#000' : 'transparent') + ';',
        'height:var(--crtz-h,auto);',
        'transition:background-color ' + MENU_FADE + ' ' + MENU_EASE + ';}',

      /* Background extension covering the icons/logo that overflow the section
         box. A pseudo-element rather than a box-shadow: an offset shadow leaves
         a gap between the box bottom and the shadow top. Collapsed to 0 unless
         the header is actually opaque. */
      '#shopify-section-header::after{',
        'content:"";position:absolute;left:0;right:0;top:100%;',
        'height:' + (BG_SOLID ? 'var(--crtz-ext,0px)' : '0') + ';',
        'background:#000;pointer-events:none;transition:none;}',

      /* Scaled about its CENTRE, which keeps the logo on the same centre line
         the theme aligns the hamburger and cart to (~53px from the section top).
         Top-anchoring it here would lift it ~9px above them. */
      '.crtz-logo-slot{',
        'position:absolute;left:50%;',
        'transform:translateX(-50%) scale(' + LOGO_SCALE + ');',
        'transform-origin:center center;',
        'z-index:10001;}',
      '.crtz-logo-slot [id="HeaderLogoWrapper"]{margin:0 !important;width:100% !important;}',

      /* Occupies the flow space the fixed header vacates. When the header is
         solid it must reserve the extension too, or the black bar would sit on
         top of the first ~40px of the page at scroll 0. */
      '.crtz-header-space{height:' +
        (BG_SOLID ? 'calc(var(--crtz-h,0px) + var(--crtz-ext,0px))'
                  : 'var(--crtz-h,0px)') + ';}',

      /* Tap targets, per TAP_INSET above. Checked that the theme defines no
         ::after/::before of its own on either element. */
      '#ToggleMobileMenu,#shopify-section-header .site-header__cart{position:relative;}',
      '#ToggleMobileMenu::after,#shopify-section-header .site-header__cart::after{',
        'content:"";position:absolute;',
        'top:-' + TAP_INSET + 'px;right:-' + TAP_INSET + 'px;',
        'bottom:-' + TAP_INSET + 'px;left:-' + TAP_INSET + 'px;}',

      /* The cart link is display:inline in the theme, and inline elements ignore
         transform - needed for CART_NUDGE and ICON_SCALE below. */
      '#shopify-section-header .site-header__cart{display:inline-block;}',

      /* Note the tap targets above scale with these, 16px -> ~15px effective.
         Still clears 44x44: hamburger lands ~58x49, cart ~54x54. */
      /* --crtz-lift raises the icons to sit centred in the bar. A transform
         rather than margins or `top`: it doesn't disturb the row's layout, and
         the theme already puts its own `top: 25px` on the hamburger. Applied
         here rather than on .top-bar because a transform there would become the
         containing block for the absolutely-positioned logo slot and knock it
         off centre horizontally. */
      '#ToggleMobileMenu{',
        'transform:translateY(calc(-1 * var(--crtz-lift,0px))) scale(' + ICON_SCALE + ');',
        'transform-origin:center left;}',
      '#shopify-section-header .site-header__cart{',
        'transform:translateY(calc(-1 * var(--crtz-lift,0px))) scale(' + ICON_SCALE + ');',
        'transform-origin:center right;}',

      /* Optical size match with the hamburger. Anchored centre-right so growing
         it doesn't push the icon off the edge or off the shared centre line. */
      (CART_NUDGE !== 1 ?
        '#shopify-section-header .site-header__cart svg{' +
          'transform:scale(' + CART_NUDGE + ');transform-origin:center right;}'
      : ''),

      /* The theme locks scrolling for its mobile menu with
         body{position:fixed;top:-<scrollY>} plus html{overflow:hidden}. Both are
         fatal to position:sticky - the header gets laid out against a body that
         has been shifted upwards and renders off-screen (at -600px in testing).
         The header is already fixed, so nothing about its positioning changes
         here and no layout moves - only the offset and the background.

         top:0 rather than --crtz-top: that same body shift takes any sticky
         banner off-screen with it (measured at -2000px), so keeping the offset
         would leave a gap where the banner isn't. */
      'html.menu-open #shopify-section-header{',
        'top:0;',
        'background-color:#000;}',
      'html.menu-open #shopify-section-header::after{height:0;}',

      /* used only while measuring, so reads aren't taken mid-transition */
      'html.crtz-measuring #shopify-section-header{transition:none !important;}',
    '}',

    '@media screen and (max-width:749px) and (prefers-reduced-motion:reduce){',
      '#shopify-section-header{transition:none !important;}',
    '}'
  ].join('');

  var styleEl = document.createElement('style');
  styleEl.setAttribute('data-crtz-sticky-header', '');
  styleEl.appendChild(document.createTextNode(CSS));
  (document.head || document.documentElement).appendChild(styleEl);

  /* ---- behaviour --------------------------------------------------------- */
  var root = document.documentElement;
  var sec, slot, logo, origParent, origNext;
  var cachedW = 0, cachedTop = 0, cachedAtW = 0, reserve = 0, spacer = null;
  var bars = [], barRO = null, refreshTimer = null, resizeTimer = null;

  /* Viewport width the current slot was measured at. A width change inside the
     mobile range never crosses the media query, so this is the only thing that
     can tell us the cached geometry no longer describes the layout. */
  var lastW = 0;

  /* Holds the flow space the fixed header would otherwise vacate, so the page
     sits where it always did and opening the menu moves nothing. */
  function ensureSpacer() {
    if (spacer && spacer.parentNode) return;
    spacer = document.createElement('div');
    spacer.className = 'crtz-header-space';
    spacer.setAttribute('aria-hidden', 'true');
    sec.parentNode.insertBefore(spacer, sec.nextSibling);
  }

  function removeSpacer() {
    if (spacer && spacer.parentNode) spacer.parentNode.removeChild(spacer);
    spacer = null;
  }

  /* Debounced re-measure, for things that fire in bursts. */
  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { if (mq.matches) measure(); }, 150);
  }

  /* Watch the banners we found so a height change (or the countdown ending)
     re-offsets the header without needing a scroll or resize. */
  function watchBars() {
    if (!window.ResizeObserver) return;
    if (barRO) barRO.disconnect();
    else barRO = new ResizeObserver(refresh);
    bars.forEach(function (b) { barRO.observe(b); });
  }

  function build() {
    sec = document.getElementById('shopify-section-header');
    if (!sec) return false;
    if (slot) return true;

    logo = [].slice.call(document.querySelectorAll('[id="HeaderLogoWrapper"]'))
      .filter(function (n) { return n.getBoundingClientRect().width > 20; })[0];

    var bar = sec.querySelector('.top-bar .grid__item.text-right') ||
              sec.querySelector('.top-bar');
    if (!logo || !bar) return false;

    /* Capture the logo's rendered geometry BEFORE moving it - once it is out of
       the sidebar's layout, both its width and its offset are unrecoverable.

       The section must be un-stuck for this. While it is sticky and the page is
       scrolled, the section is pinned to the viewport top while the logo is
       still down in the sidebar, so (logo.top - section.top) comes out wildly
       negative and throws the logo off-screen. Dropping position to static for
       the measurement puts both back in the same coordinate space. */
    var prevPos = sec.style.position;
    sec.style.position = 'static';
    var lr = logo.getBoundingClientRect();
    var sr = sec.getBoundingClientRect();
    sec.style.position = prevPos;

    var logoW   = Math.round(lr.width);
    var logoH   = Math.round(lr.height);
    var logoTop = Math.round(lr.top - sr.top);

    /* Reuse the last known-good numbers if this measurement looks wrong, so a
       bad read degrades to a slightly misplaced logo rather than a missing one.

       Only when they were taken at THIS width, though. The fallback is meant to
       cover a momentarily bad read at the current size; reused across a resize
       it silently places the logo using an offset that belonged to a different
       layout, which is worse than the bad read it was guarding against. */
    if (logoW > 20 && logoTop >= 0 && logoTop < 300) {
      cachedW = logoW; cachedTop = logoTop; cachedAtW = window.innerWidth;
    } else if (cachedW && cachedAtW === window.innerWidth) {
      logoW = cachedW; logoTop = cachedTop;
    } else {
      return false;   /* nothing trustworthy to place it with - leave it alone */
    }

    origParent = logo.parentElement;
    origNext   = logo.nextSibling;

    /* Reserve the space the logo is vacating so the page doesn't jump up.

       Fall back to the logo's own height rather than trusting the parent's
       measurement alone: if the parent reads 0 here (it can, depending on when
       this runs relative to layout), nothing gets reserved and the mobile menu -
       which is laid out after this space - slides up underneath the close
       button. The logo is an aspect-ratio padding box, so its height is sound
       whenever its width is, and build() already refuses to run without a
       sane width. */
    reserve = Math.max(Math.round(origParent.getBoundingClientRect().height), logoH);

    slot = document.createElement('div');
    slot.className = 'crtz-logo-slot';
    slot.style.width = logoW + 'px';
    slot.style.top   = logoTop + 'px';
    bar.appendChild(slot);
    slot.appendChild(logo);
    reserveLogoSpace();
    return true;
  }

  /* Re-applied on every measure(), not just at build time - build() bails early
     once the slot exists, so a reservation set from a bad first read would
     otherwise never be corrected. */
  function reserveLogoSpace() {
    if (origParent && reserve) origParent.style.minHeight = reserve + 'px';
  }

  /* Undo everything when the viewport crosses above the breakpoint - e.g. a
     phone rotated to landscape, which is ~844px and clears 749. */
  function restore() {
    if (slot) {
      if (origParent) {
        origParent.insertBefore(logo, origNext);
        origParent.style.minHeight = '';
      }
      if (slot.parentNode) slot.parentNode.removeChild(slot);
      slot = null;
    }
    removeSpacer();

    /* Clear everything measure() wrote. --crtz-lift is the one that matters:
       it is a CORRECTION to the lift already applied, so it is self-correcting
       only while the cluster it was measured from is still on screen. Left in
       place across a trip to desktop it seeds the next mobile pass with a number
       taken at a different width, and the error compounds on every round trip -
       measured at 30px off the icon centre line after one, 45px after two.

       Unconditional rather than inside the slot check above: measure() can also
       run via refresh() after a failed build, when there is no slot to remove
       but --crtz-lift has still been set. */
    if (sec) {
      sec.style.removeProperty('--crtz-lift');
      sec.style.removeProperty('--crtz-h');
      sec.style.removeProperty('--crtz-ext');
      sec.style.removeProperty('--crtz-top');
      sec.style.height = '';
    }
    lastW = 0;
  }

  /* Campaigns sometimes add a banner above the header - currently the Essential
     Countdown Timer app, which injects a `position:sticky; top:0` element as the
     first child of <body>, outside the theme entirely. Our header is also sticky
     at top:0 with a far higher z-index (10000 vs 1), so the moment the page
     scrolls the two occupy the same strip and the icons draw over the banner.

     Rather than hard-code that app's class, measure any sticky/fixed bar pinned
     to the top that sits before the header, and push the header down past it.
     No banner means an offset of 0 and nothing changes. */
  function stickyBarsAbove() {
    var off = 0, kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === sec) break;              /* only things ABOVE the header count */
      if (el.nodeType !== 1) continue;
      var cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
      var t = parseFloat(cs.top);
      if (isNaN(t) || t > 4) continue;    /* pinned to the top, not mid-page */
      var r = el.getBoundingClientRect();
      if (r.height < 1) continue;
      off += r.height;
      bars.push(el);
    }
    return Math.round(off);
  }

  /* Vertical extent of everything visible in the header - icons and logo -
     measured from the section's own top. */
  function contentExtent() {
    var st = sec.getBoundingClientRect().top;
    var top = Infinity, bottom = -Infinity;
    ['#ToggleMobileMenu', '#shopify-section-header .site-header__cart', '.crtz-logo-slot']
      .forEach(function (s) {
        var e = document.querySelector(s);
        if (!e) return;
        var r = e.getBoundingClientRect();
        if (r.height < 1) return;
        top    = Math.min(top, r.top - st);
        bottom = Math.max(bottom, r.bottom - st);
      });
    return (top === Infinity) ? null : { top: top, bottom: bottom, height: bottom - top };
  }

  /* How far the lowest header element sits below the section's own box. */
  function overflowBelow(secH) {
    var st = sec.getBoundingClientRect().top, low = secH;
    ['#ToggleMobileMenu', '#shopify-section-header .site-header__cart', '.crtz-logo-slot']
      .forEach(function (s) {
        var e = document.querySelector(s);
        if (e) low = Math.max(low, e.getBoundingClientRect().bottom - st);
      });
    return Math.max(0, Math.ceil(low - secH) + PAD);
  }

  function measure() {
    if (!sec) return;

    root.classList.add('crtz-measuring');

    /* Offset past any sticky banner sitting above us. Recomputed every pass, so
       a banner that appears, resizes or is dismissed is picked up. */
    bars = [];
    sec.style.setProperty('--crtz-top', stickyBarsAbove() + 'px');
    watchBars();
    reserveLogoSpace();
    ensureSpacer();

    sec.style.height = 'auto';

    /* Centre the icon/logo cluster in the bar: PAD_Y of black above and below,
       measured from under the red rule. Sizing the bar around its contents is
       also why there's no longer a separate height formula - the height IS the
       content plus its padding.

       Computed as a CORRECTION to the lift already applied, rather than by
       resetting to zero and re-measuring. Both forms are equivalent when reads
       are perfectly fresh, but this one is self-correcting: the whole cluster is
       uniformly offset by the current lift, so any pass that reads slightly
       stale simply gets fixed by the next one instead of compounding. */
    var rule  = sec.querySelector('.page-border');
    var ruleH = rule ? Math.round(rule.getBoundingClientRect().height) : 0;
    var c     = contentExtent();
    var h;

    if (c) {
      var cur  = parseFloat(sec.style.getPropertyValue('--crtz-lift')) || 0;
      var lift = Math.round(cur + (c.top - (ruleH + PAD_Y)));
      h = Math.round(ruleH + PAD_Y + c.height + PAD_Y);
      sec.style.setProperty('--crtz-lift', lift + 'px');
      if (slot) slot.style.top = (cachedTop - lift) + 'px';
      void sec.offsetHeight;
    } else {
      h = Math.round(sec.getBoundingClientRect().height);
    }

    sec.style.height = h + 'px';
    sec.style.setProperty('--crtz-h', h + 'px');
    sec.style.setProperty('--crtz-ext', overflowBelow(h) + 'px');

    /* Hand height back to the stylesheet, then force a reflow so dropping
       crtz-measuring can't animate the reset. */
    sec.style.height = '';
    void sec.offsetHeight;
    root.classList.remove('crtz-measuring');
  }

  var mq = window.matchMedia(MQ);

  function sync() {
    if (mq.matches) {
      /* Rebuild from scratch when the width has changed since the slot was
         made. build() bails early once the slot exists and measure() reuses
         cachedTop, so without this the logo keeps an offset captured at the old
         width. Tearing down and rebuilding reuses the paths that already work
         rather than introducing a second way to place the logo. */
      if (slot && window.innerWidth !== lastW) restore();
      if (!build()) return;
      lastW = window.innerWidth;
      measure();
    } else {
      restore();
    }
  }

  function init() {
    sync();

    /* Re-run after load and once more shortly after: the logo is lazyloaded, so
       its geometry settles late. sync() rather than measure() so a build that
       failed earlier gets retried - measure() alone can't create the slot. */
    window.addEventListener('load', function () { if (mq.matches) sync(); });
    setTimeout(function () { if (mq.matches) sync(); }, 600);

    if (mq.addEventListener) { mq.addEventListener('change', sync); }
    else if (mq.addListener) { mq.addListener(sync); }

    /* Banner apps inject at the top of <body> whenever their script happens to
       run, which can be well after us. childList only (not subtree), so the
       countdown ticking over every second doesn't trigger anything. */
    if (window.MutationObserver) {
      new MutationObserver(refresh)
        .observe(document.body, { childList: true });

    }

    window.addEventListener('orientationchange', function () {
      setTimeout(sync, 250);
    });

    /* Plain resizes, which nothing else here covers. matchMedia only fires when
       the 749px boundary is crossed, so any width change within the mobile range
       used to leave the logo at its old offset.

       This is also the retry path for a build() that failed at the crossing.
       The load and 600ms retries are both guarded by mq.matches, so they are
       skipped entirely when the page happens to load at desktop width - after
       which nothing ever tried again, and the logo was left unmanaged. */
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sync, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
